import { RefObject, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ERROR_CODES } from '~/constants/errorCodes.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import useGetUserRole from '~/hooks/useGetUserRole';
import { useAppDispatch } from '~/store/appDispatch';
import {
  diiaSignGetFinalStatusAssetManager,
  diiaSignGetStatusAssetManager,
  diiaSignInitializeAssetManager,
  diiaSignRetrieveDocsAssetManager,
} from '~/store/diia/thunks/signatureAssetManagerThunks';
import {
  diiaSignGetFinalStatus,
  diiaSignGetStatus,
  diiaSignInitialize,
  diiaSignRetrieveDocs,
} from '~/store/diia/thunks/signatureThunks';
import { DiiaStatuses } from '~/store/diia/types';
import { setIdentificationError } from '~/store/identification-form/slices';
import {
  deleteDocumentsInvalidSignatures,
  deleteDocumentsInvalidSignaturesAssetManager,
  regenerateOwnershipSchemeAfterReports,
  regenerateReports,
  validateDocumentSign,
  validateDocumentSignAssetManager,
} from '~/store/identification-form/thunks/documentThunks';
import * as Sentry from '@sentry/react';
import { setDiiaSignError } from '~/store/diia/slices';
import { saveSignsFromIIT, startIITSigning } from '~/store/iit/thunks';
import { setIITError } from '~/store/iit/slices';
import { TIITSign } from '~/store/iit/types';
import { TDocument, TDocumentsInStore } from '../types';
import { resetIITLib } from '~/libs/iit/lib';
import { selectIdentification } from '~/store/identification-form/selectors';
import { selectTokenData } from '~/store/users/selectors';
import { selectIIT } from '~/store/iit/selectors';
import { selectDiia } from '~/store/diia/selectors';
import { TimerHandle } from '~/components/Timer/Timer';
import { TThunkError } from '~/store/covenants/types';
import store from '~/store/store.ts';
import { findDocumentById } from '~/modules/identification/forms/OwnershipStructure/utils/utils.tsx';
import { WEB_DOCS, WebDocName } from '~/constants/docs.ts';

interface IuseSignActionsProps {
  applicationId: string;
  timerRef: RefObject<TimerHandle | null>;
  setGetStatusPromise: (promise: { abort: () => void } | null) => void;
  closeModal: () => void;
  forceBpmRequest: () => void;
  documentsToSign: TDocument[];
  isFirstSignAttemptCount: boolean;
}

export const useSignActions = ({
  applicationId,
  timerRef,
  setGetStatusPromise,
  closeModal,
  documentsToSign,
  isFirstSignAttemptCount,
  forceBpmRequest,
}: IuseSignActionsProps) => {
  const dispatch = useAppDispatch();
  const tokenData = useSelector(selectTokenData);
  const { loading: identificationLoading } = useSelector(selectIdentification) || {};
  const { loading: isDiiaLoading, error: diiaError, status } = useSelector(selectDiia) || {};
  const { isLoading: IITLoading } = useSelector(selectIIT) || {};
  const { isAssetManager } = useGetUserRole();
  const [requestId, setRequestId] = useState<string>('');

  const isLoading = identificationLoading || isDiiaLoading || IITLoading;

  const handleSignatureError = (error: unknown) => {
    const errorBody = (error as { data: TThunkError })?.data || error;
    const code = errorBody.errorCode;

    let message;
    if (ERROR_CODES.INTERNAL_SERVER_ERROR === Number(code)) {
      message = errorMessages.DOCUMENT_SIGNING_FAILED;
    } else if (
      [ERROR_CODES.SIGNATURE_INIT_ERROR, ERROR_CODES.DOCUMENT_FETCH_FAILED].includes(Number(code))
    ) {
      message = errorBody.message;
    }

    dispatch(setIdentificationError({ errorMessage: message }));
  };

  useEffect(() => {
    (async () => {
      if (status === DiiaStatuses.rejected && requestId) {
        try {
          if (isAssetManager) {
            await dispatch(
              diiaSignGetFinalStatusAssetManager({
                requestId,
                applicationId,
                managerId: tokenData.managerId,
              })
            ).unwrap();
          } else {
            dispatch(diiaSignGetFinalStatus({ requestId, applicationId })).unwrap();
          }
        } catch {
          handleSignatureError(diiaError || { status: DiiaStatuses.canceled });
        }
      }
    })();
  }, [status, requestId]);

  const handleDiiaSignatureAssetManager = async () => {
    if (isLoading) return;

    try {
      if (!(await handleDocumentsInvalidSignatures())) return;

      const { requestId } = await dispatch(
        diiaSignInitializeAssetManager({ applicationId, managerId: tokenData.managerId })
      ).unwrap();
      setRequestId(requestId);
      timerRef.current?.startTimer();

      Sentry.logger.info('SignDocuments', { action: 'diiaSignInitializeAssetManager' });
      const params = {
        requestId,
        managerId: tokenData.managerId,
        applicationId,
      };
      const promise = dispatch(diiaSignGetStatusAssetManager(params));

      Sentry.logger.info('SignDocuments', { action: 'diiaSignGetStatusAssetManager' });

      setGetStatusPromise(promise);
      const { status } = await promise.unwrap();
      setGetStatusPromise(null);

      if (status === DiiaStatuses.notUsed) {
        dispatch(setDiiaSignError(errorMessages.DOCUMENT_SIGNING_FAILED));
      }

      if (status === DiiaStatuses.processing) {
        const finalStatus = await dispatch(diiaSignGetFinalStatusAssetManager(params)).unwrap();

        Sentry.logger.info('SignDocuments', { action: 'diiaSignGetFinalStatusAssetManager' });

        if (finalStatus.status === DiiaStatuses.done) {
          closeModal();
          await dispatch(diiaSignRetrieveDocsAssetManager(params)).unwrap();
          await handleSignatureValidation();
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      handleSignatureError(error);
    }
  };

  const handleDiiaSignature = async () => {
    if (isLoading) return;

    try {
      if (!(await handleDocumentsInvalidSignatures())) return;

      const { requestId } = await dispatch(diiaSignInitialize({ applicationId })).unwrap();
      Sentry.logger.info('SignDocuments', { action: 'diiaSignInitialize' });
      setRequestId(requestId);
      timerRef.current?.startTimer();

      const promise = dispatch(diiaSignGetStatus({ requestId, applicationId }));
      setGetStatusPromise(promise);
      const { status } = await promise.unwrap();
      Sentry.logger.info('SignDocuments', { action: 'diiaSignGetStatus' });
      setGetStatusPromise(null);

      if (status === DiiaStatuses.notUsed) {
        dispatch(setDiiaSignError(errorMessages.DOCUMENT_SIGNING_FAILED));
      }

      if (status === DiiaStatuses.processing) {
        const finalStatus = await dispatch(
          diiaSignGetFinalStatus({ requestId, applicationId })
        ).unwrap();

        Sentry.logger.info('SignDocuments', { action: 'diiaSignGetFinalStatus' });

        if (finalStatus.status === DiiaStatuses.done) {
          closeModal();
          await dispatch(diiaSignRetrieveDocs({ requestId, applicationId })).unwrap();
          await handleSignatureValidation();
        }
      } else {
        throw new Error(errorMessages.SERVICE_UNAVAILABLE);
      }
    } catch (error) {
      Sentry.captureException(error);
      handleSignatureError(error);
    }
  };

  const handleDocumentsInvalidSignatures = async () => {
    if (isFirstSignAttemptCount) return true;
    if (isLoading) return;

    try {
      if (isAssetManager) {
        await dispatch(
          deleteDocumentsInvalidSignaturesAssetManager({
            applicationId,
            managerId: tokenData.managerId,
          })
        ).unwrap();
      } else {
        await dispatch(deleteDocumentsInvalidSignatures({ applicationId })).unwrap();

        const { mainDocuments } =
          (store.getState().identification.formData.documents as TDocumentsInStore)?.documents ||
          {};
        const hadSchemeBefore = !!findDocumentById(
          mainDocuments,
          WEB_DOCS[WebDocName.ownershipStructureScheme]
        )?.idDoc;

        const docs = await dispatch(regenerateReports({ applicationId })).unwrap();

        await dispatch(
          regenerateOwnershipSchemeAfterReports({
            docs: docs!,
            appId: applicationId!,
            hadSchemeBefore,
          })
        ).unwrap();
      }
      return true;
    } catch (error) {
      dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
      return false;
    }
  };

  const handleIITSignature = async () => {
    if (isLoading) return;
    if (!(await handleDocumentsInvalidSignatures())) return;
    if (isAssetManager) {
      try {
        await dispatch(
          startIITSigning({
            path: `/identification/asset-managers/${tokenData.managerId}/screen-forms/application/${tokenData.appId}/documents/signature-verification/hashes-only`,
          })
        ).unwrap();
      } catch (error) {
        Sentry.captureException(error);
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      }
    } else {
      try {
        await dispatch(
          startIITSigning({
            path: `/identification/application/${applicationId}/documents/signature-verification/hashes-only`,
          })
        ).unwrap();
      } catch (error) {
        Sentry.captureException(error);
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      }
    }
  };

  const onIITSign = async (signFiles: TIITSign[]) => {
    try {
      let res;
      if (isAssetManager) {
        res = await dispatch(
          saveSignsFromIIT({
            fileList: signFiles,
            path: `/identification/asset-managers/${tokenData.managerId}/screen-forms/application/${tokenData.appId}/documents/signed`,
          })
        ).unwrap();
      } else {
        res = await dispatch(
          saveSignsFromIIT({
            fileList: signFiles,
            path: `/identification/application/${applicationId}/documents/signed`,
          })
        ).unwrap();
      }

      const uploadedDocuments = res?.uploadedDocuments || [];
      const unSavedDocuments = res?.unSavedDocuments || [];

      if (!unSavedDocuments.length || uploadedDocuments.length >= documentsToSign.length) {
        await handleSignatureValidation();
      } else {
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      }
    } catch (error) {
      Sentry.captureException(error);
      dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
    } finally {
      resetIITLib();
    }
  };

  const handleSignatureValidation = async () => {
    try {
      let response;
      if (isAssetManager) {
        response = await dispatch(
          validateDocumentSignAssetManager({
            managerId: tokenData.managerId,
            applicationId: tokenData.appId,
          })
        ).unwrap();
      } else {
        response = await dispatch(validateDocumentSign({ applicationId })).unwrap();
      }

      const hasKepVerificationStatus = Object.keys(response.documentKepVerificationStatus).length;
      if (hasKepVerificationStatus) {
        throw new Error(errorMessages.SERVICE_UNAVAILABLE);
      }
      forceBpmRequest();
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setIdentificationError({ errorMessage: errorMessages.SIGNATURE_VERIFICATION_FAILED })
      );
    }
  };

  return { handleIITSignature, onIITSign, handleDiiaSignature, handleDiiaSignatureAssetManager };
};
